/* eslint-disable mastery/known-imports */
import { APP_USE_LOCALE_FOR_DATE_TIME } from '@app/GlobalVariables/constants';
import { MODE } from '@env';
import {
  CalendarDate,
  parseAbsolute,
  parseZonedDateTime,
  TimeFields,
  ZonedDateTime,
} from '@internationalized/date';
import { t } from '@lingui/core/macro';
import { jsonStringify } from '@utils/json';
import { DEBUG_STRING, hasLocalizationDebug } from '@utils/localize';
import {
  addDays as addDaysFns,
  differenceInCalendarDays,
  differenceInMilliseconds,
  endOfDay,
  // eslint-disable-next-line no-restricted-imports
  format,
  isAfter as isAfterFn,
  isBefore as isBeforeFn,
  isValid,
  parse,
  startOfDay,
  subDays as subDaysFns,
} from 'date-fns';
import {
  compact,
  debounce,
  isNil,
  isNumber,
  isString,
  isUndefined,
  last,
  memoize,
  range,
  throttle,
  uniq,
} from 'lodash-es';
import timezones from 'timezones.json';
import { MergeExclusive } from 'type-fest';
import { GET_ISO_DATE_PART_FIX } from '../../app/GlobalVariables/constants';
import { getGlobalVariable } from '../../app/GlobalVariables/util';
import { CURRENT_LOCALE } from './util';

const shouldUseDebug = hasLocalizationDebug();

/** @deprecated Do not use! Use getDatetimeValue instead, which supports internationalization. */
export const dateFnsFormat = format;

export const MaxDate = new Date(8640000000000000);

export const millisToIntlSetObj = (ms: number): TimeFields => {
  const secs = ms / 1000;
  const hour = Math.floor(secs / 3600);
  const minute = Math.floor((secs % 3600) / 60);
  const second = Math.floor(secs % 60);
  const millisecond = Math.floor(ms % 1000);
  return {
    hour,
    minute,
    second,
    millisecond,
  };
};

export enum TimeZone {
  EST = 'America/New_York',
  CST = 'America/Chicago',
  MST = 'America/Denver',
  PST = 'America/Los_Angeles',
  AST = 'America/Anchorage',
  HonoluluT = 'Pacific/Honolulu',
}
export type IANATimezones =
  | 'America/Anchorage'
  | 'America/Los_Angeles'
  | 'America/Phoenix'
  | 'America/Denver'
  | 'America/Chicago'
  | 'America/New_York'
  | 'UTC'
  | 'Asia/Tokyo'
  | string;

/** The user's current timezone. */
export const currentTZ: IANATimezones =
  (Intl.DateTimeFormat().resolvedOptions().timeZone as IANATimezones) ||
  'America/Chicago';

/** @deprecated You should not need this. Use DatetimeValue component or getDatetimeValue util. */
export const getTimezone = memoize((str: string): string => {
  let tz = str;
  if (tz === 'UTC') {
    return 'Etc/UTC';
  }
  // The following case is for legacy timezones like UTC or CDT. We want values like America/Chicago instead
  if (tz.length < 5) {
    tz = timezones.find((obj) => obj.abbr === str)?.utc[0] || '';
  }
  return tz;
});

export const getDateFromDatetime = (
  str: string,
  /** Use leading zeros for month and day, like 02/03. Defaults to true */
  rawLeadingZero?: boolean,
  withYear?: boolean,
): string => {
  const useLeadingZero = rawLeadingZero === undefined ? true : rawLeadingZero;
  let result = str;
  if (str.match(/^\d?\d\/\d?\d\/\d\d\d\d/)) {
    // MM/DD/YYYY
    result = withYear
      ? str.replace(/^(\d?\d\/\d?\d\/\d\d\d\d).*/, '$1')
      : str.replace(/^(\d?\d\/\d?\d).*/, '$1');
  } else if (str.match(/^\d?\d\/\d?\d\/\d\d/)) {
    // MM/DD/YY
    result = withYear
      ? str.replace(/^(\d?\d\/\d?\d\/\d\d)/, '$1')
      : str.replace(/(.*\d?\d\/\d?\d)\/\d\d/, '$1');
  } else if (str.match(/^(\d\d\d\d)-(\d\d)-(\d\d)/)) {
    // YYYY-MM-DD
    result = withYear
      ? str.replace(/^(\d\d\d\d)-(\d\d)-(\d\d).*/, '$1/$2/$3')
      : str.replace(/^\d\d\d\d-(\d\d)-(\d\d).*/, '$1/$2');
  } else if (str.match(/^(\d\d)-(\d\d)-(\d\d)/)) {
    // YY-MM-DD
    result = withYear
      ? str.replace(/^(\d\d)-(\d\d)-(\d\d).*/, '$1/$2/$3')
      : str.replace(/^(\d\d)-(\d\d)-\d\d.*/, '$1/$2');
  }
  if (useLeadingZero) {
    result = result
      .split('/')
      .map((n) => n.padStart(2, '0'))
      .join('/');
  }
  return result;
};

/** @deprecated If you need to display a date to a user, use `DatetimeValue` component or `getDatetimeValue` helper. */
export const DEPRECATEDgetTimezoneAwareDateFormattedMMDDYY = (
  dateObj: Date,
  tz?: string,
): string => {
  return dateObj.toLocaleString('en-US', {
    timeZone: tz ? getTimezone(tz) : undefined,
    hour12: false,
    dateStyle: 'short',
  } as fixMe);
};

/** @deprecated Use server utilities like `serverUtilityUTCDateToLocal` for constructing queries or mutations. If you need to display a date to a user, use `DatetimeValue` component or `getDatetimeValue` helper. */
export const DEPRECATEDgetDateFromLocaleDatetime = (
  dateObj: Date,
  tz?: string,
  withYear?: boolean,
): string => {
  return getDateFromDatetime(
    DEPRECATEDgetTimezoneAwareDateFormattedMMDDYY(dateObj, tz),
    undefined,
    withYear,
  );
};

/** NOT FOR DISPLAYING TO USERS. Create a date string from a date object in a format (defaults to 'MM/DD/YYYY') that is timezone aware. ie takes in UTC date and turns it into a localized date string based on tz. */
export const serverUtilityUTCDateToLocal = (
  str: Maybe<string | Date>,
  tz: Maybe<string>,
  format: 'MM/DD/YYYY' | 'YYYY-MM-DD' = 'MM/DD/YYYY',
): string | undefined => {
  if (!str || !tz) {
    return undefined;
  }
  const dateObj = new Date(str);
  if (isValid(dateObj)) {
    const tt = parseAbsolute(dateObj.toISOString(), tz ?? currentTZ);

    if (format === 'MM/DD/YYYY') {
      return [tt.month, tt.day, tt.year]
        .map((str) => str.toString().padStart(2, '0'))
        .join('/');
    } else if (format === 'YYYY-MM-DD') {
      return [tt.year, tt.month, tt.day]
        .map((str) => str.toString().padStart(2, '0'))
        .join('-');
    }
  }
  return undefined;
};
/** NOT for user display. Use `getDatetimeValue` for rendering dates + times. This utility should only be used to send data to an API (ie date filtering on a query). */
export const serverUtilityFormatDateFromCurrentTimezone = (
  date: Date,
  strFormat: 'yyyy-MM-dd',
): string => {
  return format(date, strFormat);
};

/** @deprecated Use `getDatetimeValue` for rendering to user. Use `serverUtilityUTCDateToLocal` to create a date string that is for the server in that is always in `MM/DD/YYYY` format. */
export const DEPRECATEDutcDatetimeToLocalDate = (
  /** The utcDatetime string like 2019-12-18T21:43:10.587Z */
  str: Maybe<string | Date>,
  /** The timezone like America/Chicago */
  tz: Maybe<string>,
  rawLeadingZero?: boolean,
  withYear?: boolean,
  showShortTZ?: boolean,
): string | undefined => {
  if (!str) {
    return undefined;
  }
  try {
    const dateObj = new Date(str);
    if (isValid(dateObj)) {
      const rawStr = dateObj.toLocaleString(CURRENT_LOCALE, {
        timeZone: tz,
        hour12: false,
        ...(showShortTZ && {
          timeZoneName: 'short',
        }),
      } as fixMe);
      const dateStr = getDateFromDatetime(rawStr, rawLeadingZero, withYear);
      if (!showShortTZ) {
        return dateStr;
      }
      const timezoneStr = last((rawStr || '').split(' '));
      return compact([dateStr, timezoneStr]).join(' ');
    }
  } catch {
    // err
  }
  return undefined;
};

// eslint-disable-next-line no-console
const dateSatisfyReportThrottled = throttle((err) => console.error(err), 5000);

/** Get the 24hr time from a date string or Date object, given a timezone. Returns a string like "08:00" or "14:30" */
export const getTimeFromDate = (
  date: string | Date | undefined,
  timezone: string,
): string | undefined => {
  if (shouldUseDebug) {
    return DEBUG_STRING;
  }
  if (!date || !timezone) {
    return undefined;
  }
  const dateObj = new Date(date);
  if (!isValid(dateObj)) {
    return undefined;
  }
  const parsed = parseAbsolute(dateObj.toISOString(), timezone);
  return (
    parsed.hour.toString().padStart(2, '0') +
    ':' +
    parsed.minute.toString().padStart(2, '0')
  );
};

export const DEPRECATEDgetTimeFromDatetimeString = (str: string): string => {
  if (!str) {
    return '';
  }
  const matches = str.match(/(\d\d:\d\d)/) || [];
  if (matches[1]) {
    const a = matches[1];
    return a === '24:00' ? '00:00' : a;
  } else {
    dateSatisfyReportThrottled(`${str} does not satisfy a valid Date`);
  }
  return str;
};

// without the cache, the getTimeInTimezone function is much slower, as it is expensive to instantiate a new Intl.DateTimeFormat object
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();
function getTimeInTimezone(date: Date, timezone: string): string {
  let formatter = timeFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(CURRENT_LOCALE, {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
    timeFormatterCache.set(timezone, formatter);
  }
  return formatter.format(date);
}

/** @deprecated Use DatetimeValue component or getDatetimeValue fn with the `timeOnly` prop. Convert a utc datetime string to a LOCAL time like 8:00:00 PM CST */
export const DEPRECATEDUtcDatetimeToLocalTime = (
  /** The utcDatetime string like 2019-12-18T21:43:10.587Z */
  str: string | Date,
  /** The timezone like America/Chicago */
  tz: string,
): string | undefined => {
  try {
    const dateObj = new Date(str);
    if (isValid(dateObj)) {
      return DEPRECATEDgetTimeFromDatetimeString(
        getTimeInTimezone(dateObj, tz).replace(/24(:\d\d.*)/, '00$1'),
      );
    }
  } catch (error) {
    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }
  return undefined;
};

/** @deprecated - Use shiftDateByTimezone instead. ie shiftDateByTimezone(date, currentTZ, 'Etc/UTC') */
export const changeDateTimezoneToUtc = (
  str: string | null | undefined,
): Date | null => {
  if (!str) {
    return null;
  }
  const localDate = new Date(str);
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds(),
      localDate.getMilliseconds(),
    ),
  );
};

interface GetDatetimeShortOptions {
  showYear?: boolean;
  showShortTZ?: boolean;
}
/** @deprecated Use `getDatetimeValue` instead. Get a string like "12/19/19 11:25". Default options of value and timezone for getDatetimeValue are the same. */
export const DEPRECATEDGetDatetimeShort = (
  rawVal: Maybe<string | Date>,
  tz?: string,
  options: GetDatetimeShortOptions = {
    showYear: true,
    showShortTZ: false,
  },
): string => {
  let d = rawVal;
  if (!d) {
    return '';
  }
  if (typeof d === 'string') {
    d = new Date(d);
  }
  const { showYear = true, showShortTZ = false } = options;
  return (
    d
      .toLocaleString('en-US', {
        hour12: false,
        ...(showYear && {
          year: '2-digit',
        }),
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: tz ? getTimezone(tz) : currentTZ,
        ...(showShortTZ && {
          timeZoneName: 'short',
        }),
      } as anyOk)
      .replace(/,/g, '')
      // chrome comes up with a time of 24:00 as of this comment writing, when the value should actually be 00:00
      // to try it:
      // new Date('2020-02-14T00:00:00').toLocaleString('en-US', {hour12: false})
      // firefox doesn't do this
      .replace(/\s24:(\d\d)/, ' 00:$1')
      .trim()
  );
};

/** @deprecated use DatetimeValue component or getDatetimeValue fn with the `timeOnly` prop. */
export const getLocalDatetimeShort = (
  d: Date,
  options: { showYear: boolean } = { showYear: true },
): string => {
  return DEPRECATEDGetDatetimeShort(d, undefined, {
    showYear: options.showYear,
  });
};

/** Take a date and convert it to local string, given a timezone */
const toBaseLocaleString = (d: Date, tz: string): string => {
  return (
    d
      .toLocaleString('en-US', {
        hour12: true,
        timeZone: getTimezone(tz),
      })
      // convert non-breaking spaces into regular spaces (Chrome introduced this in v110). This helps date-fns parse the date correctly.
      .replace(/[\u00A0\u1680\u180E\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
  );
};

/** Take date and convert it to a local Date object, given a timezone */
export const dateToTimezoneDate = (str: string | Date, tz: string): Date => {
  const ogDate = new Date(str);
  return parse(toBaseLocaleString(ogDate, tz), 'P, pp', ogDate);
};

/** Test if two dates are on the same calendar day, taking timezone into account. 2:00am in NY is the same day as 1:00am in Chicago. 2:00am in NY is not the same day as 8:00pm in Los Angeles */
export const isSameDay = (
  left: { value: Date; timezone: string },
  right: { value: Date; timezone: string },
): boolean => {
  const a = parseAbsolute(left.value.toISOString(), left.timezone);
  const b = parseAbsolute(right.value.toISOString(), right.timezone);
  return a.day === b.day;
};

/** Get the difference in milliseconds between a utc date object, and a given IANA timezone
 *  This is analagous to getting the "offset" of a timezone, but should be more bulletproof as it will respect DST more accurately.
 */
export const getTimezoneDiff = (utcDate: Date, tz: string): number =>
  differenceInMilliseconds(utcDate, dateToTimezoneDate(utcDate, tz));

/** Mutates to true UTC time from user's local time, taking tz into account */
export const userLocalDateToUTC = (str: string | Date, tz: string): Date => {
  if (MODE !== 'production' && typeof str === 'string' && !str.endsWith('Z')) {
    throw new Error(
      'The ISO Date string passed to this function does not include the Z string at the end. This produces unexpected cross-browser results.',
    );
  }
  const ogDate = new Date(str);
  const localTime = dateToTimezoneDate(str, tz);
  const diffInMillis = ogDate.valueOf() - localTime.valueOf();
  return new Date(ogDate.valueOf() + diffInMillis);
};

/** Mutates to local time from UTC time, taking tz into account */
export const utcDateToLocal = (str: string | Date, tz: string): Date => {
  return dateToTimezoneDate(str, tz);
};

/** @deprecated use `getTimeFromDate` instead */
export const DEPRECATEDgetTimeFromLocaleDatetime = (
  dateObj: Date,
  tz: string,
): string => {
  return DEPRECATEDgetTimeFromDatetimeString(
    DEPRECATEDGetDatetimeShort(dateObj, tz),
  );
};

export interface FormatRangeArgs {
  start: Maybe<string | Date>;
  end: Maybe<string | Date>;
  tz: string;
  showShortTZ?: boolean;
  includeTimes?: boolean;
  includeDayOfWeek?: boolean;
  withYear?: boolean;
}

/** @deprecated Use DateRangeValue component or getDateRangeValue util */
export const formatDateRange = ({
  start,
  end,
  tz,
  includeTimes,
  includeDayOfWeek,
  withYear,
}: FormatRangeArgs): string => {
  let startStr = '';
  if (start) {
    startStr = DEPRECATEDgetDateFromLocaleDatetime(
      new Date(start),
      tz,
      withYear,
    );
  }
  let startTime = '';
  if (includeTimes && start) {
    startTime = DEPRECATEDgetTimeFromLocaleDatetime(new Date(start), tz);
  }
  let endStr = startStr;
  if (end) {
    endStr = DEPRECATEDgetDateFromLocaleDatetime(new Date(end), tz, withYear);
  }
  let endTime = '';
  if (includeTimes && end) {
    endTime = DEPRECATEDgetTimeFromLocaleDatetime(new Date(end), tz);
  }
  let dayOfWeekStart = '';
  if (includeDayOfWeek && start) {
    dayOfWeekStart = new Date(start).toLocaleDateString(undefined, {
      weekday: 'short',
    });
  }
  let dayOfWeekEnd = '';
  if (includeDayOfWeek && end) {
    dayOfWeekEnd = new Date(end).toLocaleDateString(undefined, {
      weekday: 'short',
    });
  }
  if (includeTimes) {
    const startPrefix = compact([dayOfWeekStart, startStr, startTime]).join(
      ' ',
    );
    const endPrefix = compact([dayOfWeekEnd, endStr, endTime]).join(' ');
    if (startStr === endStr) {
      return `${startPrefix} - ${endTime}`;
    } else {
      return `${startPrefix} - ${endPrefix}`;
    }
  }
  return compact(uniq([startStr, endStr])).join(' - ');
};

const isoDateRegex = /\d\d\d\d-\d\d-\d\dT\d\d:\d\d/;

// eslint-disable-next-line no-console
const consoleErrorDebounced = debounce(console.error, 3000, { leading: true });

export const checkForValidCoercion = (dateStr: string | Date): Date => {
  const startDate = new Date(dateStr);
  if (MODE !== 'production') {
    if (!isValid || (isString(dateStr) && !isoDateRegex.test(dateStr))) {
      consoleErrorDebounced(
        new Error(
          `Value passed to formatTimeRange start of ${dateStr} cannot be coerced to a valid Date object.`,
        ),
      );
    }
  }
  return startDate;
};

// intl formatter that outputs time only as 24 hour
const timeOnlyFormatterCache = new Map<string, Intl.DateTimeFormat>();
export function getTimeOnlyInTimezone(date: Date, timezone: string): string {
  const enableLocale = getGlobalVariable(APP_USE_LOCALE_FOR_DATE_TIME);
  const userLocale = enableLocale ? CURRENT_LOCALE : 'en-US';
  const cacheKey = `${timezone}|${userLocale}`;
  let formatter = timeOnlyFormatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(userLocale, {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
    timeOnlyFormatterCache.set(cacheKey, formatter);
  }
  return formatter.format(date);
}

const showShortTZFormatterCache = new Map<string, Intl.DateTimeFormat>();
export const getShortTZFromDateAndTimezone = (
  date: Date,
  timezone: string,
): string => {
  const enableLocale = getGlobalVariable(APP_USE_LOCALE_FOR_DATE_TIME);
  const userLocale = enableLocale ? CURRENT_LOCALE : 'en-US';
  const cacheKey = `${timezone}|${userLocale}`;
  let formatter = showShortTZFormatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(userLocale, {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    showShortTZFormatterCache.set(cacheKey, formatter);
  }
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find((part) => part.type === 'timeZoneName');
  return tzPart ? tzPart.value : '';
};

/**
 * @deprecated Use {@link getTimeRangeValue} from the TimeRangeValue component instead.
 */
// ts-unused-exports:disable-next-line
export const DEPRECATEDformatTimeRange = ({
  start,
  end,
  tz,
  showShortTZ,
}: FormatRangeArgs): string => {
  let startStr = '',
    timezoneStr = '';
  let startDate: Date | undefined;
  if (start) {
    startDate = checkForValidCoercion(start);
    startStr = getTimeOnlyInTimezone(startDate, tz);
  }
  let endStr = startStr;
  if (end) {
    const endDate = checkForValidCoercion(end);
    endStr = getTimeOnlyInTimezone(endDate, tz);
  }
  if (showShortTZ && startDate) {
    timezoneStr = ' ' + getShortTZFromDateAndTimezone(startDate, tz);
  }
  return `${compact(uniq([startStr, endStr])).join(' - ')}${timezoneStr}`;
};

export const TimeStringToMsOffsetUTC = (
  timeString?: string,
): number | undefined => {
  if (!timeString) {
    return undefined;
  }
  // Converts string in HH:mm or h:mm 24-hour format to number of Milliseconds offset from UNIX epoch.
  // Returns undefined for empty or invalid timeString.
  if (!timeString.length || !timeString.match(/[0-2]?\d:\d\d/)) {
    return undefined;
  }
  const theDate = new Date(0);
  timeString = timeString.padStart(5, '0'); //Pad leading 0 if it's missing.
  theDate.setUTCHours(parseInt(timeString.substring(0, 2)));
  theDate.setUTCMinutes(parseInt(timeString.substring(3)));
  return theDate.getTime();
};

export const MsOffsetUTCToTimeString = (
  timeOffsetMs: number | string,
): string => {
  // Takes a number of Milliseconds offset from UNIX epoch and converts to 24-hour time.
  // Can be used with Formik which passes value in as string or with functions passing value in as a number.
  let timeOffsetAsNumber;
  if (typeof timeOffsetMs === 'number') {
    timeOffsetAsNumber = timeOffsetMs;
  } else if (typeof timeOffsetMs === 'string' && timeOffsetMs.length) {
    timeOffsetAsNumber = parseInt(timeOffsetMs);
  }
  if (!isUndefined(timeOffsetAsNumber)) {
    const theDate = new Date();
    theDate.setTime(timeOffsetAsNumber);
    return DEPRECATEDgetTimeFromDatetimeString(theDate.toUTCString());
  } else {
    return '';
  }
};

/** Takes ms and provides a Date object back (1970 epoch) */
export const MsOffsetToDate = (
  timeOffsetMs: number | string,
): Date | undefined => {
  let timeOffsetAsNumber;
  if (typeof timeOffsetMs === 'number') {
    timeOffsetAsNumber = timeOffsetMs;
  } else if (typeof timeOffsetMs === 'string' && timeOffsetMs.length) {
    timeOffsetAsNumber = parseInt(timeOffsetMs);
  }
  if (!isUndefined(timeOffsetAsNumber)) {
    const theDate = new Date();
    theDate.setTime(timeOffsetAsNumber);
    return theDate;
  }
  return undefined;
};

export const getDateMillis = (d: Date): number => {
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();
  return hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;
};

export const dateToMsOffset = (
  originalDate?: Date | string | null,
  tz = 'Etc/UTC',
): number | undefined => {
  if (originalDate === undefined || originalDate === null) {
    return undefined;
  }
  const d = shiftDateByTimezone(new Date(originalDate), tz, currentTZ);
  return getDateMillis(d);
};

export const format24HrTimeOffsetRange = (
  startMSOffset: number | null,
  endMSOfset: number | null,
): string => {
  const vals = [startMSOffset, endMSOfset].filter(isNumber);
  return uniq(vals.map(MsOffsetUTCToTimeString)).join(' - ');
};

export const getTimezoneAbbreviation = memoize((str: string): string => {
  return timezones.find((obj) => obj.utc.find((tz) => tz == str))?.abbr || '';
});

const formatsWithYear: Array<[string, RegExp]> = [
  ['MM/dd/yyyy', /^\d\d\/\d\d\/\d\d\d\d$/],
  ['yyyy/MM/dd', /^\d\d\d\d\/\d\d\/\d\d$/],
  ['MM/dd/yy', /^\d\d\/\d\d\/\d\d$/],
  ['MM/dd/yy', /^\d\/\d\d\/\d\d$/],
  ['MM/dd/yy', /^\d\d\/\d\/\d\d$/],
  ['MM/dd/yy', /^\d\/\d\/\d\d$/],
  ['MM/dd/yy', /^\d\d\d\d\d\d\d\d$/],
  ['MM/dd/yyyy', /^\d\/\d\d\/\d\d\d\d$/],
  ['MM/dd/yyyy', /^\d\/\d\/\d\d\d\d$/],
  ['MM/d/yyyy', /^\d\d\/\d\/\d\d\d\d$/],
];

const supportedFormats: Array<[string, RegExp]> = formatsWithYear.concat([
  ['MM/dd', /^\d\d\/\d\d$/],
  ['MM/d', /^\d\d\/\d$/],
  ['M/dd', /^\d\/\d\d$/],
  ['M/d', /^\d\/\d$/],
  ['MMdd', /^\d\d\d\d$/],
  ['MMd', /^\d\d\d$/],
  ['MM', /^\d\d$/],
]);

export const parseStringDate = (rawStr: string): Date | undefined => {
  try {
    const str = (rawStr || '').replace(/[-.]/g, '/').trim();
    for (const [template, matcher] of supportedFormats) {
      if (str.match(matcher)) {
        return parse(str, template, new Date());
      }
    }
  } catch {
    // noop
  }
  return undefined;
};

export const createDateWithTimezone = (
  str: string,
  tz: string,
): Date | undefined => {
  const parsed = parseStringDate(str);
  if (!parsed) {
    return undefined;
  }
  const diff = getTimezoneDiff(parsed, tz);
  return new Date(parsed.valueOf() + diff);
};

/** This is a util for DatePicker component and very likely should not be used in other contexts. */
export const getZonedStartOfDayForTimezone = (
  dz: ZonedDateTime | CalendarDate,
  tz: string,
): ZonedDateTime | undefined => {
  const { year, month, day } = dz;
  if (!year || !month || !day || !tz) {
    return undefined;
  }
  return parseZonedDateTime(
    `${year.toString().padStart(4, '0')}-${month
      .toString()
      .padStart(2, '0')}-${day.toString().padStart(2, '0')}[${tz}]`,
  );
};

/** Mutates the date by shifting it N number of hours away from the user's timezone. Helpful for things like the Datepicker which always assumes local time. */
export const shiftDateByTimezone = (
  d: Date,
  tzFrom: string,
  tzTo: string,
): Date => {
  const userTZ = tzFrom || currentTZ;
  const fromDate = dateToTimezoneDate(d, userTZ);
  const toDate = dateToTimezoneDate(d, tzTo);
  const diff = (fromDate?.valueOf() || 0) - (toDate?.valueOf() || 0);
  return new Date(d.valueOf() + diff);
};

enum DayOfWeek {
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
}

const allDayOfWeekStrings = Object.values(DayOfWeek).filter(isString);

/** Get the US day of week for a given Date and timezone. ie "Wednesday" */
// ts-unused-exports:disable-next-line
export const getUSDayOfWeek = (
  rawDate: Maybe<Date | string>,
  tz: string,
): string | undefined => {
  if (!rawDate) {
    return undefined;
  }
  return new Date(rawDate).toLocaleDateString('en-US', {
    weekday: 'long',
    timeZone: getTimezone(tz),
  });
};

/** Get day of week for a given Date and timezone. Lowercase - ie "wednesday" */
export const getUSDayOfWeekLowercase = (
  rawDate: Maybe<Date | string>,
  tz: string,
): DayOfWeek | undefined => {
  return getUSDayOfWeek(rawDate, tz)?.toLowerCase() as unknown as DayOfWeek;
};

export const getStartOfDay = (
  rawDate: Maybe<Date | string>,
  tz: string,
): Date | undefined => {
  if (!rawDate) {
    return undefined;
  }
  const shiftedDate = shiftDateByTimezone(new Date(rawDate), tz, currentTZ);
  return shiftDateByTimezone(startOfDay(shiftedDate), currentTZ, tz);
};

export const currentFullYear = new Date().getFullYear();

/** @deprecated DO NOT USE */
const DEPRECATEDgetISODatePart = (
  rawDate: Maybe<Date | string>,
  /** Defaults to currentTZ, like a date generated by Datepicker */
  tz?: string,
): string | undefined => {
  if (!rawDate) {
    return undefined;
  }
  const newRawDate = new Date(rawDate);
  if (!isValid(newRawDate)) {
    return undefined;
  }
  return shiftDateByTimezone(newRawDate, tz ?? currentTZ, 'Etc/UTC')
    .toISOString()
    .slice(0, 10);
};

/** Get a ISO-compliant date like 2022-04-12 that takes TZ into account. As of writing, Datepicker component defaults to user's current TZ. To get a date part for use in some APIs, we shift the date by UTC offset and use ISOString. This util is good for when locale is NOT involved. */
export const getISODatePart = (
  rawDate: Maybe<Date | string>,
  tz?: string,
): string | undefined => {
  const useNew = getGlobalVariable(GET_ISO_DATE_PART_FIX);
  if (!useNew) {
    return DEPRECATEDgetISODatePart(rawDate, tz);
  }

  if (!rawDate) {
    return undefined;
  }

  const dateObj = new Date(rawDate);
  if (!isValid(dateObj)) {
    return undefined;
  }

  const parsed = parseAbsolute(dateObj.toISOString(), tz ?? currentTZ);

  return `${parsed.year.toString().padStart(4, '0')}-${parsed.month
    .toString()
    .padStart(2, '0')}-${parsed.day.toString().padStart(2, '0')}`;
};

export const getEndOfDay = (
  rawDate: Maybe<Date | string>,
  tz: string,
): Date | undefined => {
  if (!rawDate) {
    return undefined;
  }
  const shiftedDate = shiftDateByTimezone(new Date(rawDate), tz, currentTZ);
  return shiftDateByTimezone(endOfDay(shiftedDate), currentTZ, tz);
};

interface BaseProps {
  date: Date | string;
  timezone?: string;
}

interface WithTime {
  time: Date | number | string;
}

interface WithTimeOffset {
  timeOffsetMs: number;
}

type CombineDateAndTimeArgs = BaseProps &
  MergeExclusive<WithTime, WithTimeOffset>;

/** Combine two dates, one that signifies the day to be taken, the other the time to be taken. Used in some of our forms where we have two independent inputs. The function assumes both values originate from the same timezone. */
export const combineDateAndTime = ({
  date,
  time,
  timeOffsetMs,
  timezone: tzProp,
}: CombineDateAndTimeArgs): Date => {
  // Explicitly handle empty string use case
  const timezone = tzProp || currentTZ;
  const parsedTime = parseAbsolute(
    time ? new Date(time).toISOString() : new Date().toISOString(),
    timezone,
  );
  const parsedDate = parseAbsolute(
    date ? new Date(date).toISOString() : new Date().toISOString(),
    timezone,
  );
  if (time) {
    return parsedDate
      .set({
        hour: parsedTime.hour,
        minute: parsedTime.minute,
        second: parsedTime.second,
        millisecond: parsedTime.millisecond,
      })
      .toDate();
  } else if (timeOffsetMs) {
    return parsedDate.set(millisToIntlSetObj(timeOffsetMs)).toDate();
  }
  return parsedDate.toDate();
};

export const getTodayWithTimezone = (): Date | undefined => {
  return createDateWithTimezone(format(new Date(), 'yyyy-MM-dd'), currentTZ);
};

export const getTodayLocalAsUtc = (): Date | undefined => {
  const now = new Date();
  const yyyy = now.getFullYear().toString().padStart(4, '0');
  const mm = (now.getMonth() + 1).toString().padStart(2, '0');
  const dd = now.getDate().toString().padStart(2, '0');
  return createDateWithTimezone(`${yyyy}-${mm}-${dd}`, 'UTC');
};

export const sortByDate = (a: string, b: string): number => {
  const a1 = new Date(a).valueOf();
  const b1 = new Date(b).valueOf();
  if (a1 < b1) {
    return 1;
  } else if (a1 > b1) {
    return -1;
  } else {
    return 0;
  }
};

/** Takes a zone like America/Chicago and returns a string like CST or CDT */
export const getTimezoneAbbr = (
  ianaTimezoneString: Maybe<string>,
  date?: Maybe<Date>,
): string | undefined => {
  if (!ianaTimezoneString) {
    return undefined;
  }
  const d = createDateWithTimezone(
    format(date || new Date(), 'yyyy-MM-dd'),
    ianaTimezoneString,
  );
  if (d) {
    const parsed = DEPRECATEDGetDatetimeShort(d, ianaTimezoneString, {
      showYear: false,
      showShortTZ: true,
    });
    const found = last(parsed.split(' '));
    // IANA timezone abbr list seems to require at least two characters
    // https://en.wikipedia.org/wiki/List_of_time_zone_abbreviations
    if (found?.match(/\w\w/)) {
      return found;
    }
  }
  return undefined;
};

export const getFormattedStartEndAvailable = (
  stop: Maybe<{
    availableStart?: Maybe<{
      value?: Maybe<string>;
      timezone?: Maybe<string>;
    }>;
    availableEnd?: Maybe<{
      value?: Maybe<string>;
      timezone?: Maybe<string>;
    }>;
  }>,
  tz: Maybe<string>,
  showYear?: boolean,
): string => {
  if (!stop) {
    return '';
  }
  return formatDateRange({
    start: stop.availableStart?.value,
    end: stop.availableEnd?.value,
    tz: stop.availableStart?.timezone || tz || currentTZ,
    withYear: showYear,
  });
};

interface BetweenDaysKwargs {
  start: Maybe<Date | string>;
  startTimezone: Maybe<string>;
  end: Maybe<Date | string>;
  endTimezone: Maybe<string>;
  /** Include start and end in array */
  inclusive?: boolean;
}

const getDifferenceInCalendarDaysWithinRange = (
  kwargs: BetweenDaysKwargs,
): number | undefined => {
  const { start, startTimezone, end, endTimezone } = kwargs;
  if (!start || !startTimezone || !end || !endTimezone) {
    return undefined;
  }
  return differenceInCalendarDays(new Date(end), new Date(start));
};

export const getDatesWithinRange = (
  kwargs: BetweenDaysKwargs,
): Array<Date> | undefined => {
  const { start, startTimezone, end, endTimezone, inclusive } = kwargs;
  if (!start || !startTimezone || !end || !endTimezone) {
    return undefined;
  }
  const diff = differenceInCalendarDays(new Date(end), new Date(start));
  const middle = range(diff).map((i) => {
    return addDaysFns(new Date(start), i + 1);
  });
  return compact([inclusive && new Date(start), ...middle]);
};

const arrIsFull = <T extends unknown>(arr: Maybe<T>[]): arr is T[] => {
  return !arr.find(isNil);
};

/** Returns named days like "sunday", "monday" that are between two dates. Named days will not repeat, ie if the dates are further than 1 week apart, only 7 named days will be returned. */
export const getNamedDaysBetweenDates = (
  kwargs: BetweenDaysKwargs,
): undefined | DayOfWeek[] => {
  const { start, startTimezone, end, endTimezone } = kwargs;
  if (!start || !startTimezone || !end || !endTimezone) {
    return undefined;
  }
  const num = getDifferenceInCalendarDaysWithinRange(kwargs);
  if (isNumber(num) && num > 30) {
    return allDayOfWeekStrings as anyOk;
  }
  const dates = getDatesWithinRange(kwargs);
  const arr = dates?.map((d) => {
    if (!kwargs.startTimezone) {
      return undefined;
    }
    return getUSDayOfWeekLowercase(d, kwargs.startTimezone);
  });
  if (!arr || !arrIsFull(arr)) {
    return undefined;
  }
  return uniq(arr);
};

interface TimeSpanValue {
  amount: number;
  label?: string;
  display: string;
}

interface TimeSpanObj {
  days: TimeSpanValue;
  hours: TimeSpanValue;
  minutes: TimeSpanValue;
}

interface minutesToTimespanObjProps {
  minutesInput: number;
  abbreviate?: boolean;
}

const minutesToTimespanObj = (args: minutesToTimespanObjProps): TimeSpanObj => {
  const { minutesInput, abbreviate = false } = args;
  const hours: TimeSpanValue = {
    amount: Math.floor(minutesInput / 60),
    display: '',
    label: '',
  };
  const minutes: TimeSpanValue = {
    amount: Math.floor(minutesInput - hours.amount * 60),
    display: '',
    label: '',
  };
  const days: TimeSpanValue = {
    amount: Math.floor(hours.amount / 24),
    display: '',
    label: '',
  };
  hours.amount = Math.floor(hours.amount - days.amount * 24);

  if (days.amount > 0) {
    if (days.amount === 1) {
      days.label = `day`;
    } else {
      days.label = `days`;
    }
    days.display = `${days.amount} ${days.label}`;
  }

  if (
    (days.amount == 0 && hours.amount == 0) ||
    (hours.amount == 0 && minutes.amount == 0) ||
    (days.amount != 0 && hours.amount == 0 && minutes.amount != 0)
  ) {
    hours.label = '';
  } else {
    if (hours.amount === 1) {
      hours.label = abbreviate ? 'hr' : 'hour';
    } else {
      hours.label = abbreviate ? 'hrs' : 'hours';
    }
    hours.display = `${hours.amount} ${hours.label}`;
  }

  if (minutes.amount > 0) {
    if (minutes.amount === 1) {
      minutes.label = abbreviate ? 'min' : 'minute';
    } else {
      minutes.label = abbreviate ? 'min' : 'minutes';
    }
    minutes.display = `${minutes.amount} ${minutes.label}`;
  }

  return { days, hours, minutes };
};

/* example: 1442 minutes => 1 day, 0 hour, 2 minutes */
export const minutesToTimespan = (minutesInput: Maybe<number>): string => {
  if (!minutesInput || minutesInput < 0) {
    return '';
  }

  const { days, hours, minutes } = minutesToTimespanObj({ minutesInput });

  const dSeparator =
    days.amount == 0 || (hours.amount == 0 && minutes.amount == 0) ? '' : ', ';

  const hSeparator =
    (days.amount == 0 && hours.amount == 0) ||
    (hours.amount == 0 && minutes.amount == 0) ||
    (days.amount != 0 && hours.amount == 0 && minutes.amount != 0) ||
    minutes.amount == 0
      ? ''
      : ', ';

  return `${days.display}${dSeparator}${hours.display}${hSeparator}${minutes.display}`;
};

/** Get the distance between two dates, with 2 degrees of resolution. ie "1 hr, 30 min" or "2 days, 5 hrs". Potentially useful for display in a table cell. If you want a humanized, localized display like "12 hours ago", use `getTimeAgoValue`. */
export const getTimeDistance = (args: {
  base?: Date | string;
  compare?: Date | string;
}): string | null => {
  const { base: oldDateRaw, compare: newDateRaw = new Date() } = args;
  if (!oldDateRaw) {
    return null;
  }
  const oldDate = new Date(oldDateRaw);
  const newDate = new Date(newDateRaw);
  const diff = newDate.getTime() - oldDate.getTime();
  const diffInMinutes = Math.floor(diff / (1000 * 60));
  const { days, hours, minutes } = minutesToTimespanObj({
    minutesInput: diffInMinutes,
    abbreviate: true,
  });
  let result = '';
  if (hours.amount < 1) {
    result = `${minutes.display}`;
  } else if (hours.amount >= 1 && days.amount < 1) {
    result = `${hours.display} ${minutes.display}`;
  } else if (days.amount >= 1) {
    result = `${days.display} ${hours.display}`;
  }
  return result.trim();
};

/**
 * The function `getTimeZone` accepts a `Date` object, an IANA time zone identifier (e.g., "America/New_York"),
 * and a format type ('short' or 'long'). It returns the time zone name corresponding to the provided date
 * and time zone in the specified format. If the date is not provided, the function returns `undefined`.
 *
 * The format type determines the verbosity of the time zone name returned:
 * - 'short': Abbreviated timezone name (e.g., "EST", "PDT").
 * - 'long': Full timezone name (e.g., "Eastern Standard Time", "Pacific Daylight Time").
 *
 * @param {Date | undefined} date - The date for which the time zone name is needed. If undefined, the function returns undefined.
 * @param {IANATimezones} timeZone - An IANA time zone identifier (e.g., "America/New_York").
 * @param {'short' | 'long'} type - The format type for the time zone name ('short' or 'long').
 * @returns {string | undefined} The time zone name in the specified format, or undefined if the date is not provided.
 *
 * @example
 * // returns "Central Daylight Time"
 * getTimeZone(new Date(), 'America/Chicago', 'long');
 *
 * @example
 * // returns "CDT"
 * getTimeZone(new Date(), 'America/Chicago', 'short');
 */
export function getTimeZone(
  date: Date | undefined,
  timeZone: IANATimezones,
  type: 'short' | 'long',
): string | undefined {
  // Check if the date is provided, return undefined if not
  if (!date) {
    return undefined;
  }
  // Create a new Intl.DateTimeFormat object configured for the specified time zone and format type
  const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    timeZoneName: type,
  };
  const formatter = new Intl.DateTimeFormat('en-US', dateTimeFormatOptions);

  // Format the provided date using the formatter
  const formattedDateTime = formatter.format(date);

  // Extract the time zone part from the formatted date and time string
  const timeZonePart = formattedDateTime.split(',')[1]?.trim();

  // Return the extracted time zone part or undefined if it's not available
  // Example: Long: "Central Daylight Time" OR Short: "CDT"
  return timeZonePart || undefined;
}

export function getTimeZoneLabel(
  longTimeZone: string | undefined,
  shortTimeZone: string | undefined,
): string | undefined {
  if (longTimeZone && shortTimeZone) {
    return `${longTimeZone} (${shortTimeZone})`;
  } else if (longTimeZone) {
    return longTimeZone;
  }
  return undefined;
}

export const isValidDate = (val: anyOk): boolean => {
  return isValid(val);
};

/**
 * @deprecated This function is deprecated and should not be used.
 * The issue of missing 'Z' suffix on datetime strings should be fixed at the GraphQL operation level
 * instead of being patched in the UI layer. This ensures UTC timezone is properly handled from the source.
 *
 * Appends 'Z' to a datetime string if it doesn't already have a timezone indicator.
 * This was used as a workaround for datetime strings that lacked proper UTC timezone indicators.
 * Does not modify strings that already have timezone offsets (e.g., '+05:00', '-08:00').
 */
export const DEPRECATEDensureZForDatetime = (date: string): string => {
  // Don't append Z if the string already ends with Z
  if (date.slice(-1) === 'Z') {
    return date;
  }
  // Don't append Z if the string already has a timezone offset (e.g., +05:00, -08:00)
  // ISO 8601 format: ...+HH:MM or ...-HH:MM or ...+HHMM or ...-HHMM
  if (/[+-]\d{2}:?\d{2}$/.test(date)) {
    return date;
  }
  // Otherwise, append Z
  return date + 'Z';
};

// ts-unused-exports:disable-next-line
export const addDays = addDaysFns;
// ts-unused-exports:disable-next-line
export const subDays = subDaysFns;
// ts-unused-exports:disable-next-line
export const isBefore = isBeforeFn;
// ts-unused-exports:disable-next-line
export const isAfter = isAfterFn;

/**
 * Checks if the selected date is within the maximum allowable date.
 *
 * @param selectedDate - The date to check.
 * @param minDate - The minimum allowable date.
 * @param maxDate - The maximum allowable date.
 * @returns `true` if the selected date is before or on the same day as the max date, `false` otherwise.
 */
export const isDateWithinDefaultDatePickerRange = (
  selectedDate: Date | null,
  minDate: Date,
  maxDate: Date,
): boolean => {
  if (selectedDate === null || selectedDate === undefined) {
    return true;
  }

  if (isBefore(selectedDate, minDate)) {
    return false;
  }

  if (isAfter(selectedDate, maxDate)) {
    return false;
  }

  return true;
};

/**
 * Normalizes a US English day of week string to its full lowercase form.
 * Accepts formats like: SUNDAY, Sunday, Sun, Su, etc.
 * Returns the full day name in lowercase (e.g., "sunday") or undefined if invalid.
 */
const normalizeDayOfWeek = memoize((input: string): string | undefined => {
  const normalized = input.toLowerCase().trim();

  // Full day names
  const fullDayMap: Record<string, string> = {
    sunday: 'sunday',
    monday: 'monday',
    tuesday: 'tuesday',
    wednesday: 'wednesday',
    thursday: 'thursday',
    friday: 'friday',
    saturday: 'saturday',
  };

  if (fullDayMap[normalized]) {
    return fullDayMap[normalized];
  }

  // Three-letter abbreviations
  const threeLetterMap: Record<string, string> = {
    sun: 'sunday',
    mon: 'monday',
    tue: 'tuesday',
    wed: 'wednesday',
    thu: 'thursday',
    fri: 'friday',
    sat: 'saturday',
  };

  if (threeLetterMap[normalized]) {
    return threeLetterMap[normalized];
  }

  // Two-letter abbreviations
  const twoLetterMap: Record<string, string> = {
    su: 'sunday',
    mo: 'monday',
    tu: 'tuesday',
    we: 'wednesday',
    th: 'thursday',
    fr: 'friday',
    sa: 'saturday',
  };

  if (twoLetterMap[normalized]) {
    return twoLetterMap[normalized];
  }

  return undefined;
});

/**
 * Resolves a translated day of week from US English input.
 * Accepts any format like SUNDAY, Sunday, Sun, Su and returns a translated string.
 *
 * @param dayOfWeek - US English day of week string (e.g., "SUNDAY", "Sun", "Su")
 * @param options - Optional configuration
 * @param options.short - If true, returns two-letter abbreviation (default: false)
 * @returns Translated day of week string or undefined if input is invalid
 *
 * @example
 * getTranslatedDayOfWeek('SUNDAY') // Returns translated "Sunday"
 * getTranslatedDayOfWeek('Sun', { short: true }) // Returns translated "Su"
 */
export const getTranslatedDayOfWeek = (
  dayOfWeek: string,
  options: { short?: boolean } = {},
): string | undefined => {
  const normalized = normalizeDayOfWeek(dayOfWeek);
  if (!normalized) {
    return undefined;
  }

  const { short = false } = options;

  if (short) {
    // Return two-letter abbreviated forms with context for translators
    switch (normalized) {
      case 'sunday':
        return t({
          message: 'Su',
          comment: '2 letter day of week abbr for Sunday',
          context: 'day',
        });
      case 'monday':
        return t({
          message: 'Mo',
          comment: '2 letter day of week abbr for Monday',
          context: 'day',
        });
      case 'tuesday':
        return t({
          message: 'Tu',
          comment: '2 letter day of week abbr for Tuesday',
          context: 'day',
        });
      case 'wednesday':
        return t({
          message: 'We',
          comment: '2 letter day of week abbr for Wednesday',
          context: 'day',
        });
      case 'thursday':
        return t({
          message: 'Th',
          comment: '2 letter day of week abbr for Thursday',
          context: 'day',
        });
      case 'friday':
        return t({
          message: 'Fr',
          comment: '2 letter day of week abbr for Friday',
          context: 'day',
        });
      case 'saturday':
        return t({
          message: 'Sa',
          comment: '2 letter day of week abbr for Saturday',
          context: 'day',
        });
      default:
        return undefined;
    }
  } else {
    // Return full day names wrapped with t
    switch (normalized) {
      case 'sunday':
        return t`Sunday`;
      case 'monday':
        return t`Monday`;
      case 'tuesday':
        return t`Tuesday`;
      case 'wednesday':
        return t`Wednesday`;
      case 'thursday':
        return t`Thursday`;
      case 'friday':
        return t`Friday`;
      case 'saturday':
        return t`Saturday`;
      default:
        return undefined;
    }
  }
};

const durationFormatCache = new Map<string, Intl.DurationFormat>();

const getDurationFormatter = (
  locale: string,
  options: Intl.DurationFormatOptions,
): Intl.DurationFormat => {
  const key = `${locale}:${jsonStringify(options)}`;
  let formatter = durationFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DurationFormat(locale, options);
    durationFormatCache.set(key, formatter);
  }
  return formatter;
};

/**
 * Format a duration in milliseconds to a human-readable, localized string.
 * Uses Intl.DurationFormat for internationalization support.
 *
 * @param milliseconds - The total duration in milliseconds. Returns a blank
 *   string when null/undefined, and "0 min" when zero.
 * @param options - DurationFormat options (passed through to Intl.DurationFormat)
 * @returns A formatted string like "1 hr, 30 min" in English (localized in other languages)
 *
 * @example
 * formatDuration(5400000) // "1 hr, 30 min" (default style: 'short')
 * formatDuration(3600000, { style: 'long' }) // "1 hour" (60 minutes * 60 * 1000)
 * formatDuration(1800000, { style: 'narrow' }) // "30m" (30 minutes * 60 * 1000)
 * formatDuration(0) // "0 min"
 * formatDuration(null) // ""
 */
export const formatDuration = (
  milliseconds: Maybe<number>,
  options?: Intl.DurationFormatOptions,
): string => {
  if (shouldUseDebug) {
    return DEBUG_STRING;
  }

  if (milliseconds == null) {
    return '';
  }

  const enableLocale = getGlobalVariable(APP_USE_LOCALE_FOR_DATE_TIME);
  const userLocale = enableLocale ? CURRENT_LOCALE : 'en-US';

  // Convert milliseconds to duration components
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const ms = milliseconds % 1000;

  // Default options if not provided. When the duration is zero, always show
  // the minutes unit so we display "0 min" instead of an empty string.
  const formatOptions: Intl.DurationFormatOptions = options || {
    style: 'short',
    hours: hours > 0 ? 'short' : undefined,
    minutes: minutes > 0 || milliseconds === 0 ? 'short' : undefined,
    seconds: seconds > 0 ? 'short' : undefined,
    milliseconds: ms > 0 ? 'short' : undefined,
  };

  const formatter = getDurationFormatter(userLocale, formatOptions);

  return formatter.format({
    hours,
    minutes,
    seconds,
    milliseconds: ms,
  });
};

export const isTimeMidnight = (
  time: Maybe<Date | string>,
  locationTimezone: string,
): boolean => {
  if (!time) {
    return false;
  }

  const dateStr = isString(time) ? time : time.toISOString();
  const parsed = parseAbsolute(dateStr, locationTimezone);
  return parsed.hour === 0 && parsed.minute === 0;
};
